import { useState } from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import { LogoMark } from "./Logo";
import { Reveal, SectionHead } from "./Reveal";

const POS = [[50, 9], [86, 37], [72, 83], [28, 83], [14, 37]];

const Diagram = ({ items, active, setActive }) => (
  <div data-testid="network-diagram" className="relative mx-auto aspect-square w-full max-w-[580px]">
    <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" aria-hidden>
      <circle cx="50" cy="50" r="38" fill="none" stroke="rgba(244,241,234,0.14)" strokeWidth="0.2" strokeDasharray="0.6 1.4" />
      <circle cx="50" cy="50" r="22" fill="none" stroke="rgba(244,241,234,0.08)" strokeWidth="0.2" />
      {POS.map(([x, y], i) => (
        <motion.line key={`l${i}`} x1="50" y1="50" x2={x} y2={y} stroke={active === i ? "#FF5500" : "rgba(244,241,234,0.3)"} strokeWidth={active === i ? 0.45 : 0.22}
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 1.2, delay: 0.2 + i * 0.12 }} />
      ))}
      {POS.map(([x, y], i) => (
        <motion.circle key={`p${i}`} r="0.9" fill="#FF5500"
          animate={{ cx: [50, x, 50], cy: [50, y, 50], opacity: [0, 1, 0] }}
          transition={{ duration: 4, repeat: Infinity, delay: i * 0.7, ease: "easeInOut" }} />
      ))}
    </svg>
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
      <div aria-hidden className="spin-slow absolute -inset-5 rounded-full border border-dashed border-signal/50" />
      <div className="relative grid h-20 w-20 place-items-center bg-signal text-ink sm:h-24 sm:w-24"><LogoMark className="h-9 w-9 text-ink sm:h-11 sm:w-11" /></div>
    </div>
    {items.map((it, i) => (
      <button
        key={it.title}
        type="button"
        data-testid={`network-node-${i + 1}`}
        onMouseEnter={() => setActive(i)}
        onFocus={() => setActive(i)}
        onClick={() => setActive(i)}
        style={{ left: `${POS[i][0]}%`, top: `${POS[i][1]}%` }}
        className={`absolute max-w-[128px] -translate-x-1/2 -translate-y-1/2 rounded-full border px-3 py-1.5 text-center font-mono text-xs uppercase tracking-wider transition-colors duration-300 ${active === i ? "border-signal bg-signal text-ink" : "border-white/20 bg-ink/90 text-bone backdrop-blur"}`}
      >
        {it.title}
      </button>
    ))}
  </div>
);

export const Network = () => {
  const { t } = useI18n();
  const [active, setActive] = useState(0);
  const n = t.network;
  return (
    <section data-testid="network-section" className="relative overflow-hidden border-t border-white/10 bg-grid py-24 lg:py-40">
      <div className="wrap">
        <SectionHead label={n.label} title={n.title} intro={n.intro} />
        <div className="mt-16 grid items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-7 lg:order-2"><Diagram items={n.items} active={active} setActive={setActive} /></Reveal>
          <ul className="lg:col-span-5 lg:order-1">
            {n.items.map((it, i) => (
              <li key={it.title} onMouseEnter={() => setActive(i)} data-testid={`network-item-${i + 1}`} className={`border-l-2 py-4 pl-5 transition-colors duration-300 ${active === i ? "border-signal" : "border-white/10"}`}>
                <h3 className={`font-display text-xl font-semibold tracking-tight transition-colors ${active === i ? "text-signal" : "text-bone"}`}>{it.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-zinc-400">{it.desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
